import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Form as FinalForm, Field } from 'react-final-form';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import * as validators from '../../util/validators';
import { propTypes } from '../../util/types';

import { PrimaryButton } from '../Button/Button';
import FieldSelect from '../FieldSelect/FieldSelect';
import FieldTextInput from '../FieldTextInput/FieldTextInput';
import Form from '../Form/Form';

import css from './ShipStationShipFromForm.module.css';

const US_STATES = [
  ['AL', 'Alabama'], ['AK', 'Alaska'], ['AZ', 'Arizona'], ['AR', 'Arkansas'],
  ['CA', 'California'], ['CO', 'Colorado'], ['CT', 'Connecticut'], ['DE', 'Delaware'],
  ['FL', 'Florida'], ['GA', 'Georgia'], ['HI', 'Hawaii'], ['ID', 'Idaho'],
  ['IL', 'Illinois'], ['IN', 'Indiana'], ['IA', 'Iowa'], ['KS', 'Kansas'],
  ['KY', 'Kentucky'], ['LA', 'Louisiana'], ['ME', 'Maine'], ['MD', 'Maryland'],
  ['MA', 'Massachusetts'], ['MI', 'Michigan'], ['MN', 'Minnesota'], ['MS', 'Mississippi'],
  ['MO', 'Missouri'], ['MT', 'Montana'], ['NE', 'Nebraska'], ['NV', 'Nevada'],
  ['NH', 'New Hampshire'], ['NJ', 'New Jersey'], ['NM', 'New Mexico'], ['NY', 'New York'],
  ['NC', 'North Carolina'], ['ND', 'North Dakota'], ['OH', 'Ohio'], ['OK', 'Oklahoma'],
  ['OR', 'Oregon'], ['PA', 'Pennsylvania'], ['RI', 'Rhode Island'], ['SC', 'South Carolina'],
  ['SD', 'South Dakota'], ['TN', 'Tennessee'], ['TX', 'Texas'], ['UT', 'Utah'],
  ['VT', 'Vermont'], ['VA', 'Virginia'], ['WA', 'Washington'], ['WV', 'West Virginia'],
  ['WI', 'Wisconsin'], ['WY', 'Wyoming'], ['DC', 'District of Columbia'],
  ['PR', 'Puerto Rico'], ['VI', 'U.S. Virgin Islands'], ['GU', 'Guam'],
];

const CA_PROVINCES = [
  ['AB', 'Alberta'], ['BC', 'British Columbia'], ['MB', 'Manitoba'],
  ['NB', 'New Brunswick'], ['NL', 'Newfoundland and Labrador'], ['NS', 'Nova Scotia'],
  ['NT', 'Northwest Territories'], ['NU', 'Nunavut'], ['ON', 'Ontario'],
  ['PE', 'Prince Edward Island'], ['QC', 'Quebec'], ['SK', 'Saskatchewan'],
  ['YT', 'Yukon'],
];

const AU_STATES = [
  ['ACT', 'Australian Capital Territory'], ['NSW', 'New South Wales'],
  ['NT', 'Northern Territory'], ['QLD', 'Queensland'], ['SA', 'South Australia'],
  ['TAS', 'Tasmania'], ['VIC', 'Victoria'], ['WA', 'Western Australia'],
];

/**
 * ShipStationShipFromForm - Form for setting the provider's ship-from address.
 * Only shown after API key has been saved.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Additional CSS class name
 * @param {string} [props.formId] - Form identifier
 * @param {Object} [props.initialValues] - Pre-filled address fields
 * @param {Function} props.onSubmit - Save address handler
 * @param {boolean} [props.inProgress] - Whether save is in progress
 * @param {propTypes.error} [props.saveError] - Save error
 * @param {boolean} [props.ready] - Whether save succeeded
 * @returns {JSX.Element}
 */
const ShipStationShipFromForm = props => {
  const {
    className,
    formId = 'ShipStationShipFromForm',
    initialValues,
    onSubmit,
    inProgress = false,
    saveError,
    ready = false,
  } = props;

  const intl = useIntl();
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (ready) {
      setShowSuccess(true);
      const timer = setTimeout(() => setShowSuccess(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [ready]);

  const required = key => validators.required(intl.formatMessage({ id: key }));

  const classes = classNames(css.root, className);

  return (
    <FinalForm
      initialValues={initialValues}
      onSubmit={onSubmit}
      render={({ handleSubmit, invalid, pristine }) => {
        const submitDisabled = invalid || pristine || inProgress;

        return (
          <Form className={classes} onSubmit={handleSubmit}>
            <FieldTextInput
              className={css.field}
              type="text"
              id={`${formId}.shipFromName`}
              name="shipFromName"
              label={intl.formatMessage({ id: 'ShipStationShipFromForm.nameLabel' })}
              placeholder={intl.formatMessage({ id: 'ShipStationShipFromForm.namePlaceholder' })}
            />
            <FieldTextInput
              className={css.field}
              type="text"
              id={`${formId}.shipFromAddressLine1`}
              name="shipFromAddressLine1"
              label={intl.formatMessage({ id: 'ShipStationShipFromForm.addressLine1Label' })}
              placeholder={intl.formatMessage({ id: 'ShipStationShipFromForm.addressLine1Placeholder' })}
              validate={required('ShipStationShipFromForm.addressLine1Required')}
            />
            <FieldTextInput
              className={css.field}
              type="text"
              id={`${formId}.shipFromAddressLine2`}
              name="shipFromAddressLine2"
              label={intl.formatMessage({ id: 'ShipStationShipFromForm.addressLine2Label' })}
              placeholder={intl.formatMessage({ id: 'ShipStationShipFromForm.addressLine2Placeholder' })}
            />
            <FieldTextInput
              className={css.field}
              type="text"
              id={`${formId}.shipFromCity`}
              name="shipFromCity"
              label={intl.formatMessage({ id: 'ShipStationShipFromForm.cityLabel' })}
              placeholder={intl.formatMessage({ id: 'ShipStationShipFromForm.cityPlaceholder' })}
              validate={required('ShipStationShipFromForm.cityRequired')}
            />
            <Field name="shipFromCountry" subscription={{ value: true }}>
              {({ input: { value: countryValue } }) => {
                const stateLabel = intl.formatMessage({ id: 'ShipStationShipFromForm.stateLabel' });
                if (countryValue === 'US') {
                  return (
                    <FieldSelect
                      className={css.field}
                      id={`${formId}.shipFromState`}
                      name="shipFromState"
                      label={stateLabel}
                      validate={required('ShipStationShipFromForm.stateRequired')}
                    >
                      <option value="">Select a state…</option>
                      {US_STATES.map(([abbr, name]) => (
                        <option key={abbr} value={abbr}>{name}</option>
                      ))}
                    </FieldSelect>
                  );
                }
                if (countryValue === 'CA') {
                  return (
                    <FieldSelect
                      className={css.field}
                      id={`${formId}.shipFromState`}
                      name="shipFromState"
                      label={intl.formatMessage({ id: 'ShipStationShipFromForm.provinceLabel' })}
                      validate={required('ShipStationShipFromForm.stateRequired')}
                    >
                      <option value="">Select a province…</option>
                      {CA_PROVINCES.map(([abbr, name]) => (
                        <option key={abbr} value={abbr}>{name}</option>
                      ))}
                    </FieldSelect>
                  );
                }
                if (countryValue === 'AU') {
                  return (
                    <FieldSelect
                      className={css.field}
                      id={`${formId}.shipFromState`}
                      name="shipFromState"
                      label={intl.formatMessage({ id: 'ShipStationShipFromForm.stateLabel' })}
                      validate={required('ShipStationShipFromForm.stateRequired')}
                    >
                      <option value="">Select a state…</option>
                      {AU_STATES.map(([abbr, name]) => (
                        <option key={abbr} value={abbr}>{name}</option>
                      ))}
                    </FieldSelect>
                  );
                }
                return (
                  <FieldTextInput
                    className={css.field}
                    type="text"
                    id={`${formId}.shipFromState`}
                    name="shipFromState"
                    label={stateLabel}
                    placeholder={intl.formatMessage({ id: 'ShipStationShipFromForm.statePlaceholder' })}
                    validate={required('ShipStationShipFromForm.stateRequired')}
                  />
                );
              }}
            </Field>
            <FieldTextInput
              className={css.field}
              type="text"
              id={`${formId}.shipFromPostalCode`}
              name="shipFromPostalCode"
              label={intl.formatMessage({ id: 'ShipStationShipFromForm.postalCodeLabel' })}
              placeholder={intl.formatMessage({ id: 'ShipStationShipFromForm.postalCodePlaceholder' })}
              validate={required('ShipStationShipFromForm.postalCodeRequired')}
            />
            <FieldSelect
              className={css.field}
              id={`${formId}.shipFromCountry`}
              name="shipFromCountry"
              label={intl.formatMessage({ id: 'ShipStationShipFromForm.countryLabel' })}
            >
              {/* Countries where ShipStation seller accounts are available */}
              <option value="US">United States</option>
              <option value="CA">Canada</option>
              <option value="GB">United Kingdom</option>
              <option value="AU">Australia</option>
              <option value="NZ">New Zealand</option>
              <option value="FR">France</option>
              <option value="DE">Germany</option>
              <option value="IT">Italy</option>
              <option value="ES">Spain</option>
              <option value="BE">Belgium</option>
              <option value="NL">Netherlands</option>
              <option value="CH">Switzerland</option>
              <option value="PL">Poland</option>
              <option value="PT">Portugal</option>
              <option value="AT">Austria</option>
              <option value="SE">Sweden</option>
              <option value="NO">Norway</option>
              <option value="DK">Denmark</option>
              <option value="FI">Finland</option>
              <option value="IE">Ireland</option>
              <option value="SG">Singapore</option>
              <option value="MX">Mexico</option>
            </FieldSelect>

            {showSuccess && (
              <div className={css.success}>
                <FormattedMessage id="ShipStationShipFromForm.saveSuccess" />
              </div>
            )}
            {saveError && !inProgress && (
              <div className={css.error}>
                <FormattedMessage id="ShipStationShipFromForm.saveError" />
              </div>
            )}

            <PrimaryButton
              className={css.submitButton}
              type="submit"
              inProgress={inProgress}
              disabled={submitDisabled}
            >
              <FormattedMessage id="ShipStationShipFromForm.saveButton" />
            </PrimaryButton>
          </Form>
        );
      }}
    />
  );
};

ShipStationShipFromForm.propTypes = {
  formId: PropTypes.string,
  initialValues: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  inProgress: PropTypes.bool,
  saveError: propTypes.error,
  ready: PropTypes.bool,
};

export default ShipStationShipFromForm;
